import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import {
  bumpOrdersRealtime,
  markOrdersRealtimeUnseen,
} from "../app/orderApi/filterSlice";
import { dmApi } from "../app/dmApi";
import {
  connectSocket,
  onSocketEvent,
} from "../socket/socketClient";
import {
  ORDER_SOCKET_EVENTS,
  extractOrderIdsFromSocketPayload,
} from "../socket/orderSocketEvents";

/** VA dashboard queries that must soft-refresh when an order is created/updated. */
const VA_REALTIME_TAGS = [
  "VaDistributorMetrics",
  "VaHospitalOrderVolume",
  "VaHospitalsWithOrders",
];

/**
 * Socket.IO while Orders / VA dashboard surfaces are mounted.
 * Silently bumps orders list + invalidates VA metrics/hospitals (no full reload).
 */
export function useOrderSocket({ enabled = true } = {}) {
  const dispatch = useDispatch();
  const debounceRef = useRef(null);
  const acceptEventsRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      acceptEventsRef.current = false;
      return undefined;
    }

    const socket = connectSocket();
    if (!socket) return undefined;

    acceptEventsRef.current = false;

    const enableAccept = () => {
      acceptEventsRef.current = true;
    };

    const scheduleRefresh = (payload, { markUnseen = false } = {}) => {
      if (!acceptEventsRef.current) return;

      if (markUnseen) {
        const ids = extractOrderIdsFromSocketPayload(payload);
        if (ids.length) {
          dispatch(markOrdersRealtimeUnseen(ids));
        }
      }

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        dispatch(bumpOrdersRealtime());
        // Hospital list + metrics on VA dashboard (assigned order / new hospital).
        dispatch(dmApi.util.invalidateTags(VA_REALTIME_TAGS));
      }, 200);
    };

    const handleConnect = () => {
      // Short grace so handshake noise does not look like a reload.
      window.setTimeout(enableAccept, 200);
    };

    if (socket.connected) {
      handleConnect();
    }
    socket.on("connect", handleConnect);
    socket.on("reconnect", enableAccept);

    const unsubs = [
      onSocketEvent(ORDER_SOCKET_EVENTS.CREATED, (payload) =>
        scheduleRefresh(payload, { markUnseen: true })
      ),
      onSocketEvent(ORDER_SOCKET_EVENTS.UPDATED, (payload) =>
        scheduleRefresh(payload, { markUnseen: false })
      ),
    ];

    return () => {
      acceptEventsRef.current = false;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      socket.off("connect", handleConnect);
      socket.off("reconnect", enableAccept);
      unsubs.forEach((off) => off());
      // Logout calls disconnectSocket(); keep connection for order-details.
    };
  }, [enabled, dispatch]);
}
